import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { asCartRevisionConflict } from '@/api/errors';
import { Button, buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Money } from '@/components/Money';
import { Sheet } from '@/components/Sheet';
import { newIdempotencyKey } from '@/lib/ids';
import { customerApi } from './api';
import type { Cart } from './api';
import { CartLineSummary } from './CartPage';
import { useCustomer } from './context';
import {
  CartTotals,
  DraftBadge,
  ListSkeleton,
  Notice,
  PageTitle,
  Panel,
  StickyActions,
} from './parts';
import { customerKeys, useCart, usePlaceCartOrder } from './queries';
import { conflictMessage, describeCustomerError } from './wording';

/**
 * Final review before placing. The screen freezes the cart the customer is looking at and sends its
 * revision with the order. If the cart changed meanwhile the server refuses (409) and nothing is
 * placed: the customer is sent back to review the new cart and must place it again themselves.
 * There is no automatic retry and no silent merge.
 */
export function ReviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { money, session, lastSeenCart } = useCustomer();
  const cart = useCart({ refetchOnMount: 'always' });
  const place = usePlaceCartOrder();

  const [reviewed, setReviewed] = useState<Cart | null>(null);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [conflict, setConflict] = useState<string | null>(null);

  // Freeze the first cart read after opening this screen: that is what the customer reviews.
  const fresh = cart.isFetchedAfterMount ? cart.data : undefined;
  if (fresh && !reviewed) setReviewed(fresh);

  const draft = reviewed && reviewed.items.length > 0 ? <DraftBadge /> : undefined;
  const title = <PageTitle aside={draft}>Review your order</PageTitle>;

  if (!reviewed) {
    if (cart.isError) {
      return (
        <>
          {title}
          <ErrorState error={cart.error} onRetry={() => void cart.refetch()} />
        </>
      );
    }
    return (
      <>
        {title}
        <ListSkeleton rows={3} />
      </>
    );
  }

  if (reviewed.items.length === 0) {
    return (
      <>
        {title}
        <EmptyState
          icon="cart"
          title="Your cart is empty"
          message="Items you or your waiter add will appear here."
          action={
            <Link to="/t/menu" className={buttonClasses({ variant: 'primary' })}>
              Browse the menu
            </Link>
          }
        />
      </>
    );
  }

  function placeOrder(snapshot: Cart) {
    place.mutate(
      { expectedRevision: snapshot.revision, idempotencyKey },
      {
        onSuccess: (order) => {
          void navigate(`/t/orders/${order.order_id}`, {
            replace: true,
            state: { justPlaced: true },
          });
        },
        onError: async (error) => {
          if (!asCartRevisionConflict(error)) return;
          // Not placed. Read the cart as it is now so the customer can be told what changed.
          let latest: Cart | undefined;
          try {
            latest = await customerApi.getCart();
            queryClient.setQueryData(customerKeys.cart(session.sessionId), latest);
          } catch {
            latest = undefined;
          }
          lastSeenCart.current = snapshot;
          setConflict(conflictMessage(snapshot, latest));
        },
      },
    );
  }

  const backToCart = () => void navigate('/t/cart', { replace: true });
  const failure =
    place.isError && !asCartRevisionConflict(place.error)
      ? describeCustomerError(place.error, session.locationType)
      : null;

  return (
    <>
      {title}
      <Panel>
        <ul className="divide-y divide-line" aria-label="Items to order">
          {reviewed.items.map((line) => (
            <li
              key={line.cart_item_id}
              className="flex items-start justify-between gap-3 px-4 py-3"
            >
              <CartLineSummary line={line} />
              {line.line_amount !== null && (
                <Money
                  value={money(line.line_amount)}
                  className="shrink-0 text-base font-bold text-ink"
                />
              )}
            </li>
          ))}
        </ul>
      </Panel>
      <div className="mt-4">
        <CartTotals cart={reviewed} />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {!reviewed.is_valid && (
          <Notice tone="warn">
            Some items can't be ordered. Go back to your cart and remove them.
          </Notice>
        )}
        {failure && (
          <Notice tone="error">
            <strong className="block">{failure.title}</strong>
            {failure.message}
          </Notice>
        )}
        <Link
          to="/t/cart"
          className={buttonClasses({ variant: 'tertiary', fullWidth: true }) + ' mb-2'}
        >
          Back to cart
        </Link>
      </div>
      <StickyActions>
        <p className="text-center text-xs text-muted">
          Nothing is ordered until you place the order.
        </p>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={place.isPending}
          disabled={!reviewed.is_valid || conflict !== null}
          onClick={() => placeOrder(reviewed)}
        >
          Place order
        </Button>
      </StickyActions>

      <Sheet open={conflict !== null} onClose={backToCart} title="Your cart was updated">
        <p className="text-base text-ink">{conflict}</p>
        <p className="mt-2 text-sm text-muted">Your order has not been placed.</p>
        <div className="mt-5">
          <Button variant="primary" size="lg" fullWidth onClick={backToCart}>
            Review cart
          </Button>
        </div>
      </Sheet>
    </>
  );
}
